<template>
  <PopoverPortal>
    <PopoverContent
      :align="ctx.align.value"
      :side-offset="8"
      :collision-padding="10"
      class="z-popover flex flex-col rounded-md border-menu-border bg-menu text-menu-foreground shadow-lg dark:border"
      :class="[
        $style.popover_content,
        ctx.matchTriggerWidth.value && $style.match_trigger_width,
      ]"
    >
      <ListboxRoot
        :model-value="ctx.modelValue.value ?? ''"
        class="flex min-h-0 flex-col"
        @update:model-value="onSelect"
      >
        <div class="p-2 pb-0">
          <div
            class="relative flex h-8 items-center rounded border border-menu-input-border bg-menu-input focus-within:border-blue-500"
          >
            <div class="grid size-8 place-items-center">
              <MagnifyingGlassIcon class="size-4 text-menu-muted-foreground" />
            </div>

            <ListboxFilter
              v-model="ctx.query.value"
              placeholder="Search"
              class="peer h-full grow border-0 bg-transparent px-0 text-sm placeholder:text-menu-muted-foreground focus:ring-0"
            />

            <div class="flex items-center pr-2">
              <div
                v-if="ctx.isPending.value"
                class="-mr-2 grid size-8 place-items-center"
              >
                <ArrowPathIcon
                  class="size-4 animate-spin text-menu-muted-foreground"
                />
              </div>

              <button
                v-if="ctx.query.value"
                type="button"
                aria-label="Clear search"
                class="-mr-2 grid size-8 place-items-center text-menu-muted-foreground transition-colors hover:text-white"
                @click="ctx.query.value = ''"
              >
                <XMarkIcon class="size-4" />
              </button>
            </div>
          </div>
        </div>

        <ListboxContent class="overflow-y-auto py-2">
          <slot v-if="ctx.options.value.length === 0" name="empty-state">
            <div class="flex h-20 flex-col items-center justify-center">
              <p class="text-sm text-menu-muted-foreground">No items</p>
            </div>
          </slot>

          <ListboxGroup
            v-for="(children, groupName, idx) in ctx.groupedOptions.value"
            :key="groupName"
          >
            <hr v-if="idx !== 0" class="my-2 border-t border-menu-border" />

            <ListboxGroupLabel
              v-if="groupName"
              class="mx-5 my-1 shrink-0 select-none text-[0.8125rem] leading-5 text-menu-muted-foreground"
            >
              {{ groupName }}
            </ListboxGroupLabel>

            <ListboxItem
              v-for="opt in children"
              :key="opt.id"
              :value="opt.id"
              :disabled="opt.disabled"
              class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm data-[highlighted]:bg-menu-muted"
            >
              <span
                class="min-w-0 grow break-words group-data-[disabled]:opacity-50"
              >
                {{ opt.label }}
              </span>

              <CheckIcon
                class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 group-data-[state=checked]:opacity-100"
              />
            </ListboxItem>
          </ListboxGroup>
        </ListboxContent>
      </ListboxRoot>
    </PopoverContent>
  </PopoverPortal>
</template>

<script setup lang="ts">
import {
  ListboxContent,
  ListboxFilter,
  ListboxGroup,
  ListboxGroupLabel,
  ListboxItem,
  ListboxRoot,
  PopoverContent,
  PopoverPortal,
} from "reka-ui";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import MagnifyingGlassIcon from "@/icons/heroicons/magnifying-glass-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";

import { injectSearchComboboxContext } from "./context";

const ctx = injectSearchComboboxContext();

const onSelect = (next: unknown) => {
  if (typeof next === "string" && next !== "") {
    ctx.select(next);
  } else {
    ctx.select(undefined);
  }
};
</script>

<style module>
.popover_content {
  max-height: min(80dvh, var(--reka-popover-content-available-height));
  width: 320px;
  max-width: min(320px, var(--reka-popover-content-available-width));
  transform-origin: var(--reka-popover-content-transform-origin);

  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: pop-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: pop-out 250ms var(--motion-timing-ease);
    }
  }
}

.match_trigger_width {
  width: var(--reka-popover-trigger-width);
  max-width: var(--reka-popover-trigger-width);
}
</style>
